// src/pages/DevisFoodinnov.tsx

import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { db, auth } from "../firebase";
import {
  Timestamp,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import "../styles/Modal.css";
import "../styles/DevisModal.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FirestoreDevis } from "../types/firestore";
import DevisFoodinnovModal from "../components/DevisFoodinnovModal";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { CSVLink } from "react-csv";
import { logUserAction } from "../utils/logUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { getPDFCoordinatesFromStorage } from "../utils/pdfCoordinates";
import { PDF_SCALE, PDF_FONT_SIZE_OFFSET, Y_OFFSET_CORRECTION } from "../constants/pdfConfig";
import { Y_OFFSET_PDF_GENERATION, X_OFFSET_PDF_GENERATION } from "../constants/fdaConfig";
import { mmToPixels } from "../utils/units";
import { onAuthStateChanged } from "firebase/auth";
import { getUserRole } from "../utils/authUtils";

type FieldSource = 'devis' | 'opportunities' | 'society' | 'hillebrand';
type Opportunity = {
  type: string;
  amount: number;
  companyId: string;
  contact: string;
  createdAt: Date;
  date: Date;
  pu: number;
  qte: number;
  ttc: number;
  [key: string]: any; // Index signature
};
type Society = {
  siret: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  [key: string]: any; // Index signature
};

const DevisFoodinnov: React.FC = () => {
  const location = useLocation();
  const [devisList, setDevisList] = useState<FirestoreDevis[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [devisToEdit, setDevisToEdit] = useState<FirestoreDevis | undefined>(
    undefined
  );
  const [devisFoodinnovToDelete, setDevisFoodinnovToDelete] = useState<FirestoreDevis | null>(null);
  const [preselectedOpportunity, setPreselectedOpportunity] = useState<any | null>(null);


  const [contactModal, setContactModal] = useState<{
    name: string;
    phone?: string;
    email?: string;
    fonction?: string;
    gender?: string;
    principalContact?: string;
    principalFirstName?: string;
  } | null>(null);
  const [companiesMap, setCompaniesMap] = useState<any>({});

  // Nouveaux états pour les filtres et le tri
  const [filterType, setFilterType] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<number | "all">(new Date().getMonth()); // 0-indexed
  const [selectedYear, setSelectedYear] = useState<number | "all">(new Date().getFullYear());
  const [sortField, setSortField] = useState<"date" | "updatedAt" | "none">("date"); // Champ de tri par défaut
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc"); // Direction de tri par défaut
  const [userRole, setUserRole] = useState<string | null>(null); // État pour le rôle de l'utilisateur

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [devisPerPage, setDevisPerPage] = useState(10); // Modifier de const à state
  const indexOfLast = currentPage * devisPerPage;
  const indexOfFirst = indexOfLast - devisPerPage;

  const uniqueOpportunityTypes = useMemo(() => {
    const types = new Set<string>();
    devisList.forEach((d) => {
      if (d.opportunityType) {
        types.add(d.opportunityType);
      }
    });
    return Array.from(types).sort();
  }, [devisList]);

  const filtered = useMemo(() => {
    const low = searchTerm.toLowerCase();
    let tempDevis = [...devisList]; // Crée une copie mutable

    // 1. Filtrer par terme de recherche (inclut l'ID)
    tempDevis = tempDevis.filter(
      (d) =>
        (d.companyId?.toLowerCase() || "").includes(low) ||
        (d.contact?.toLowerCase() || "").includes(low) ||
        (d.opportunityType?.toLowerCase() || "").includes(low) ||
        (d.amount?.toString() || "").includes(low) ||
        (d.id?.toLowerCase() || "").includes(low)
    );

    // 2. Filtrer par type d'opportunité
    if (filterType && filterType !== "all") {
      tempDevis = tempDevis.filter((d) => d.opportunityType === filterType);
    }

    // 3. Filtrer par mois et année
    if (selectedMonth !== "all" || selectedYear !== "all") {
      tempDevis = tempDevis.filter((d) => {
        const devisDate = d.date instanceof Timestamp ? d.date.toDate() : new Date(d.date);
        const devisMonth = devisDate.getMonth();
        const devisYear = devisDate.getFullYear();

        const matchMonth = selectedMonth === "all" || devisMonth === selectedMonth;
        const matchYear = selectedYear === "all" || devisYear === selectedYear;

        return matchMonth && matchYear;
      });
    }

    // 4. Trier les devis
    if (sortField !== "none") {
      tempDevis.sort((a, b) => {
        let valA: any, valB: any;

        if (sortField === "date" || sortField === "updatedAt") {
          valA = a[sortField] instanceof Timestamp ? a[sortField].toDate() : new Date((a as any)[sortField]);
          valB = b[sortField] instanceof Timestamp ? b[sortField].toDate() : new Date((b as any)[sortField]);
        } else {
          valA = (a as any)[sortField];
          valB = (b as any)[sortField];
        }

        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return tempDevis;
  }, [devisList, searchTerm, filterType, selectedMonth, selectedYear, sortField, sortDirection]);

  const currentDevis = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / devisPerPage);

  const nextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  // Calcul du montant total HT et TTC
  const totalAmount = useMemo(() => {
    return filtered.reduce((acc, devis) => acc + (devis.amount || 0), 0);
  }, [filtered]);

  const totalAmountTTC = useMemo(() => {
    return filtered.reduce((acc, devis) => acc + ((devis.amount || 0) * 1.2), 0);
  }, [filtered]);

  // Détecter l'opportunité pré-sélectionnée depuis la navigation
  useEffect(() => {
    const state = location.state as { preselectedOpportunity?: any } | null;
    if (state?.preselectedOpportunity) {
      setPreselectedOpportunity(state.preselectedOpportunity);
      setShowModal(true);
      // Nettoyer le state pour éviter de rouvrir le modal à chaque re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Charge tous les devis et le rôle de l'utilisateur
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user && user.email) {
        const role = await getUserRole(user.email);
        setUserRole(role);
      } else {
        setUserRole(null);
      }
    });

    const docRef = doc(db, "prospects", "foodinnovCompanies");
    const unsubscribeSnapshot = onSnapshot(docRef, (snap) => {
      if (!snap.exists()) {
        setDevisList([]);
        return;
      }
      const data = snap.data();
      setCompaniesMap(data);
      console.log("Structure des données chargées pour une société (exemple):", data[Object.keys(data)[0]]);
      const all: FirestoreDevis[] = [];
      for (const companyId in data) {
        const arr = data[companyId]?.devis;
        const companyOpportunities = data[companyId]?.opportunities;
        if (Array.isArray(arr)) {
          arr.forEach((d: FirestoreDevis) =>
            all.push({ ...d, companyId, society: data[companyId], associatedOpportunities: companyOpportunities })
          );
        }
      }
      setDevisList(all);
    });
    
    return () => {
      unsubscribeAuth();
      unsubscribeSnapshot();
    };
  }, []);

  const handleAdd = () => {
    setDevisToEdit(undefined);
    setPreselectedOpportunity(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setPreselectedOpportunity(null);
  };

  const handleEdit = (d: FirestoreDevis) => {
    setDevisToEdit(d);
    setShowModal(true);
  };

  const handleSave = async (data: FirestoreDevis) => {
    try {
      // Sauvegarder dans la collection prospects/foodinnovCompanies
      const ref = doc(db, "prospects", "foodinnovCompanies");
      await setDoc(ref, {}, { merge: true });
      await updateDoc(ref, {
        [`${data.companyId}.devis`]: arrayUnion({
          ...data,
          createdAt: new Date(),
        }),
      });
      
      // Enregistrer l'action utilisateur
      const userEmail = auth.currentUser?.email;
      if (userEmail) {
        await logUserAction(
          userEmail,
          "Ajout devis",
          `${data.companyId} - ${data.opportunityType}`
        );
      }
      
      toast.success("Devis ajouté avec succès !");
    } catch (e: any) {
      console.error("Erreur ajout devis :", e);
      toast.error(`Échec de l'ajout : ${e.message}`);
    } finally {
      setShowModal(false);
    }
  };

  const handleUpdate = async (oldDevis: FirestoreDevis, newDevis: FirestoreDevis) => {
    try {
      const ref = doc(db, "prospects", "foodinnovCompanies");
      await setDoc(ref, {}, { merge: true });
      await updateDoc(ref, {
        [`${oldDevis.companyId}.devis`]: arrayRemove(oldDevis),
      });
      await updateDoc(ref, {
        [`${newDevis.companyId}.devis`]: arrayUnion({
          ...newDevis,
          createdAt: oldDevis.createdAt || new Date(),
          updatedAt: new Date(),
        }),
      });
      
      // Enregistrer l'action utilisateur
      const userEmail = auth.currentUser?.email;
      if (userEmail) {
        await logUserAction(
          userEmail,
          "Modification devis",
          `${newDevis.companyId} - ${newDevis.opportunityType}`
        );
      }
      
      toast.success("Devis mis à jour !");
    } catch (e: any) {
      console.error("Erreur mise à jour devis :", e);
      toast.error(`Échec de la mise à jour : ${e.message}`);
    } finally {
      setShowModal(false);
    }
  };

  const handleDelete = async (devis: FirestoreDevis) => {
    // Ouvre la modale de confirmation en passant l'objet devis complet
    setDevisFoodinnovToDelete(devis);
  };



  const confirmDevisFoodinnovDelete = async () => {
    if (!devisFoodinnovToDelete) return;

    console.log("Tentative de suppression du devis Foodinnov:", devisFoodinnovToDelete);
    console.log("ID du devis à supprimer:", devisFoodinnovToDelete.id);
    console.log("Company ID:", devisFoodinnovToDelete.companyId);

    try {
      const ref = doc(db, "prospects", "foodinnovCompanies");
      const companySnap = await getDoc(ref);

      if (!companySnap.exists()) {
        throw new Error("Document société non trouvé");
      }

      const companyData = companySnap.data();
      console.log("Données de la société avant filtrage:", companyData[devisFoodinnovToDelete.companyId]);

      const devisArray = companyData[devisFoodinnovToDelete.companyId]?.devis;

      if (!Array.isArray(devisArray)) {
          throw new Error("Tableau de devis non trouvé pour la société");
      }
      console.log("Tableau de devis actuel dans Firestore:", devisArray);

      // Filtrer le devis par son ID
      const updatedDevisArray = devisArray.filter((d: FirestoreDevis) => {
        console.log(`Comparaison (Foodinnov): item.id='${d.id}' (Type: ${typeof d.id}) avec devisFoodinnovToDelete.id='${devisFoodinnovToDelete.id}' (Type: ${typeof devisFoodinnovToDelete.id})`);
        return d.id !== devisFoodinnovToDelete.id;
      });
      console.log("Tableau de devis après filtrage:", updatedDevisArray);

      if (updatedDevisArray.length === devisArray.length) {
        console.warn("Aucun devis n'a été filtré (Foodinnov). L'ID pourrait ne pas correspondre.");
        toast.error("Le devis n'a pas été trouvé pour la suppression. Vérifiez l'ID.");
      }

      // Mettre à jour le document avec le nouveau tableau
      await updateDoc(ref, {
        [`${devisFoodinnovToDelete.companyId}.devis`]: updatedDevisArray,
      });
      
      const userEmail = auth.currentUser?.email;
      if (userEmail) {
        await logUserAction(
          userEmail,
          "Suppression devis",
          `${devisFoodinnovToDelete.companyId} - ${devisFoodinnovToDelete.opportunityType}`
        );
      }
      
      toast.success("Devis supprimé !");
    } catch (e: any) {
      console.error("Erreur suppression :", e);
      toast.error(`Échec de la suppression : ${e.message}`);
    } finally {
      setDevisFoodinnovToDelete(null);
    }
  };

  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [currentPdfDevis, setCurrentPdfDevis] = useState<FirestoreDevis | null>(null);
  const [showFdaPdfModal, setShowFdaPdfModal] = useState(false);
  const [fdaPdfPreviewUrl, setFdaPdfPreviewUrl] = useState<string | null>(null);

  const rgbFromHex = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return rgb(r, g, b);
  };

  const formatFieldValue = (value: any, fieldName?: string): string => {
    if (value instanceof Timestamp) {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    if (value && typeof value.toDate === 'function') {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    
    // Gestion des montants avec 2 décimales
    if (typeof value === 'number') {
      // Champs de montant connus
      const amountFields = ['amount', 'montant', 'prix', 'pu', 'ttc', 'ht', 'total', 'budget', 'cost', 'price'];
      if (fieldName && amountFields.some(field => fieldName.toLowerCase().includes(field))) {
        return value.toFixed(2);
      }
      // Si c'est un nombre et que le nom du champ contient des mots-clés de montant
      if (fieldName && (fieldName.toLowerCase().includes('amount') || 
                       fieldName.toLowerCase().includes('montant') || 
                       fieldName.toLowerCase().includes('prix') || 
                       fieldName.toLowerCase().includes('ttc') || 
                       fieldName.toLowerCase().includes('ht'))) {
        return value.toFixed(2);
      }
    }
    
    return value?.toString() || "";
  };

  // Fonction pour générer le nom de fichier PDF FDA au format: Hillebrand-FOODINNOV_Date_Client (nom société)
  const generateFdaPdfFileName = (devis: FirestoreDevis): string => {
    // Format de la date : JJ-MM-AAAA
    let dateStr = "";
    if (devis.date) {
      const date = devis.date instanceof Timestamp ? devis.date.toDate() : new Date(devis.date);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      dateStr = `${day}-${month}-${year}`;
    }

    // Nom de la société (utiliser society.name si disponible, sinon companyId)
    const companyName = (devis.society as Society)?.name || devis.companyId || "SANS_SOCIETE";
    // Nettoyer le nom de la société pour le nom de fichier (enlever les caractères spéciaux)
    const cleanCompanyName = companyName.replace(/[^a-zA-Z0-9_-]/g, '_');

    // Format final : Hillebrand-FOODINNOV_Date_Client (nom société)
    return `Hillebrand-FOODINNOV_${dateStr}_${cleanCompanyName}.pdf`;
  };

  const fillPdfWithData = async (selectedDevis: FirestoreDevis) => {
    try {
      setCurrentPdfDevis(selectedDevis);
      console.log("selectedDevis pour PDF Standard:", selectedDevis);

      const pdfPath = "/pdfs/fdaRegistrationInvoice.pdf"; // Chemin correct pour le PDF standard
      const existingPdfBytes = await fetch(pdfPath).then((res) => {
        console.log(`Statut de la réponse pour ${pdfPath}:`, res.status);
        if (!res.ok) throw new Error(`Échec du chargement du PDF: ${res.status}`);
        return res.arrayBuffer();
      });
      
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      const fontBoldItalic = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
      
      const pages = pdfDoc.getPages();
      const coords = await getPDFCoordinatesFromStorage("pdfPositions");
      const scale = PDF_SCALE;

      for (const [pageNumber, pageCoords] of Object.entries(coords)) {
        const pageIndex = parseInt(pageNumber) - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex];

        for (const [fullFieldName, fieldConfig] of Object.entries(pageCoords)) {
          const { x, y, size = 12, color, bold, italic, customText, isCustomField } = fieldConfig;
          
          let value = '';

          // Gestion des champs texte personnalisés
          if (isCustomField && customText) {
            value = customText;
            console.log(`Champ texte personnalisé: ${customText}`);
          } else {
            const [source, fieldName] = fullFieldName.includes('-') 
              ? fullFieldName.split('-') as [FieldSource, string]
              : ['devis', fullFieldName];

            // Tentative de récupérer la valeur directement si le champ existe sur selectedDevis
            // Sauf si la source est explicitement 'society'
            if (source !== 'society' && (selectedDevis as any)[fieldName] !== undefined) {
                value = formatFieldValue((selectedDevis as any)[fieldName], fieldName);
                console.log(`Récupéré directement de selectedDevis - Champ: ${fieldName}, Valeur: ${value}`);
            } else {
               switch (source) {
                  case 'devis':
                    value = formatFieldValue((selectedDevis as any)[fieldName], fieldName);
                    console.log(`Source: devis, Champ: ${fieldName}, Valeur: ${value}`);
                    break;
                  case 'opportunities':
                    console.log("Traitement source opportunities");
                    // Rechercher l'opportunité correspondante dans le tableau associatedOpportunities
                    const correspondingOpportunity = selectedDevis.associatedOpportunities?.find(
                      (opp: Opportunity) => opp.type === selectedDevis.opportunityType
                    );
                    console.log("Opportunité correspondante trouvée:", correspondingOpportunity);

                    if (correspondingOpportunity) {
                       value = formatFieldValue((correspondingOpportunity as any)[fieldName], fieldName);
                       console.log(`Source: opportunities, Champ: ${fieldName}, Valeur: ${value}`);
                    }
                    break;
                  case 'society':
                    const society = selectedDevis.society as Society | undefined;
                    if (fieldName === 'companyName') {
                      value = selectedDevis.companyId;
                    } else if (fieldName === 'address') {
                      value = society?.address || '';
                    } else {
                      value = society ? formatFieldValue(society[fieldName], fieldName) : '';
                    }
                    console.log(`Source: society, Champ: ${fieldName}, Valeur: ${value}`);
                    break;
                }
            }
          }

          if (value) {
            // Formatage final pour s'assurer que les montants ont 2 décimales
            let finalValue = value;
            if (typeof value === 'string' && !isNaN(parseFloat(value))) {
              const numValue = parseFloat(value);
              if (numValue > 0 && (fullFieldName?.toLowerCase().includes('amount') || 
                                  fullFieldName?.toLowerCase().includes('montant') || 
                                  fullFieldName?.toLowerCase().includes('prix') || 
                                  fullFieldName?.toLowerCase().includes('ttc') || 
                                  fullFieldName?.toLowerCase().includes('ht'))) {
                finalValue = numValue.toFixed(2);
              }
            }
            
            const adjustedX = x / scale;
            const adjustedY = y / scale;
            const adjustedSize = size * PDF_FONT_SIZE_OFFSET;
            
            let font = fontRegular;
            if (bold && italic) font = fontBoldItalic;
            else if (bold) font = fontBold;
            else if (italic) font = fontItalic;

            page.drawText(finalValue, {
              x: adjustedX,
              y: adjustedY,
              size: adjustedSize,
              font,
              color: color ? rgbFromHex(color) : rgb(0, 0, 0),
            });
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      setPdfPreviewUrl(blobUrl);
      setShowPdfModal(true);
      toast.success("PDF généré avec succès !");
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      toast.error("Échec de la génération du PDF");
    }
  };

  const fillFdaPdfWithData = async (selectedDevis: FirestoreDevis) => {
    try {
      setCurrentPdfDevis(selectedDevis);
      
      const pdfPath = "/pdfs/F_hillebrandfdaregistration.pdf";
      const existingPdfBytes = await fetch(pdfPath).then((res) => {
        if (!res.ok) throw new Error(`Échec du chargement du PDF: ${res.status}`);
        return res.arrayBuffer();
      });
      
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      if (pages.length === 0) throw new Error("Le PDF n'a aucune page");

      const coords = await getPDFCoordinatesFromStorage("fdaPositionsFoodinnov");
      console.log("Coordonnées FDA chargées:", coords);

      for (const [pageNumber, pageCoords] of Object.entries(coords)) {
        const pageIndex = parseInt(pageNumber) - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex];
        const { height } = page.getSize();

        for (const [fullFieldName, fieldConfig] of Object.entries(pageCoords)) {
          const { x, y, size = 12, color = "#000000", bold = false, italic = false, customText, isCustomField } = fieldConfig;
          
          let value = '';

          // Gestion des champs texte personnalisés
          if (isCustomField && customText) {
            value = customText;
            console.log(`Champ texte personnalisé FDA: ${customText}`);
          } else {
            const [source, fieldName] = fullFieldName.includes('-') 
              ? fullFieldName.split('-') as [FieldSource, string]
              : ['devis', fullFieldName];

            switch (source) {
              case 'devis':
                value = formatFieldValue((selectedDevis as any)[fieldName], fieldName);
                break;
              case 'society':
                const society = selectedDevis.society as Society | undefined;
                if (fieldName === 'companyName') {
                  value = selectedDevis.companyId;
                } else if (fieldName === 'address') {
                  value = society?.address || '';
                } else if (fieldName === 'postalCode') {
                  value = society?.postalCode || '';
                } else if (fieldName === 'city') {
                  value = society?.city || '';
                } else if (fieldName === 'country') {
                  value = society?.country || '';
                } else {
                  value = society ? formatFieldValue(society[fieldName], fieldName) : '';
                }
                break;
              case 'opportunities':
                value = formatFieldValue((selectedDevis.opportunity as Opportunity)?.[fieldName], fieldName);
                break;
              case 'hillebrand':
                if (fieldName === 'companyName') {
                  value = selectedDevis.companyId;
                } else if (fieldName === 'address') {
                  value = selectedDevis.society?.address || '';
                } else if (fieldName === 'postalCode') {
                  value = selectedDevis.society?.postalCode || '';
                } else if (fieldName === 'city') {
                  value = selectedDevis.society?.city || '';
                } else if (fieldName === 'country') {
                  value = selectedDevis.society?.country || '';
                } else if (fieldName === 'registrationNumber') {
                  value = selectedDevis.id || '';
                }
                break;
            }
          }

          if (value) {
            // Formatage final pour s'assurer que les montants ont 2 décimales
            let finalValue = value;
            if (typeof value === 'string' && !isNaN(parseFloat(value))) {
              const numValue = parseFloat(value);
              if (numValue > 0 && (fullFieldName?.toLowerCase().includes('amount') || 
                                  fullFieldName?.toLowerCase().includes('montant') || 
                                  fullFieldName?.toLowerCase().includes('prix') || 
                                  fullFieldName?.toLowerCase().includes('ttc') || 
                                  fullFieldName?.toLowerCase().includes('ht'))) {
                finalValue = numValue.toFixed(2);
              }
            }
            
            const font = await (() => {
              if (bold && italic) return pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
              if (bold) return pdfDoc.embedFont(StandardFonts.HelveticaBold);
              if (italic) return pdfDoc.embedFont(StandardFonts.HelveticaOblique);
              return pdfDoc.embedFont(StandardFonts.Helvetica);
            })();

            // Utiliser la même logique que pour les autres devis (avec PDF_SCALE)
            const scale = PDF_SCALE;
            const adjustedX = x / scale;
            const adjustedY = y / scale;
            const adjustedSize = size * PDF_FONT_SIZE_OFFSET;

            page.drawText(finalValue, {
              x: adjustedX,
              y: adjustedY,
              size: adjustedSize,
              font,
              color: rgbFromHex(color),
            });
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      setFdaPdfPreviewUrl(blobUrl);
      setShowFdaPdfModal(true);
      toast.success("PDF FDA généré avec succès !");
    } catch (error) {
      console.error("Erreur génération PDF FDA:", error);
      toast.error(`Erreur FDA PDF: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      "ID",
      "Société",
      "Contact",
      "Type",
      "Montant (HT)",
      "Montant (TTC)",
      "Date",
      "Édité le"
    ];

    const csvData = filtered.map((d) => [
      d.id || "-",
      d.companyId,
      d.contact,
      d.opportunityType,
      `${d.amount.toFixed(2)} €`,
      `${((d.amount || 0) * 1.2).toFixed(2)} €`,
      d.date instanceof Timestamp
        ? d.date.toDate().toLocaleDateString("fr-FR")
        : new Date(d.date).toLocaleDateString("fr-FR"),
      d.updatedAt
        ? d.updatedAt instanceof Timestamp
          ? d.updatedAt.toDate().toLocaleDateString("fr-FR")
          : new Date(d.updatedAt).toLocaleDateString("fr-FR")
        : "-"
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `devis_foodinnov_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openContactModal = (devis: FirestoreDevis) => {
    const companyInfo = companiesMap[devis.companyId];
    setContactModal({
      name: devis.contact ?? "Contact inconnu",
      email: companyInfo?.principalEmail ?? "Non renseigné",
      phone: companyInfo?.principalPhone ?? "Non renseigné",
      fonction: companyInfo?.principalPosition ?? "",
      gender: companyInfo?.principalGender,
      principalContact: companyInfo?.principalContact,
      principalFirstName: companyInfo?.principalFirstName,
    });
  };

  const closeContactModal = () => setContactModal(null);

  return (
    <div className="devis-container">
      <ToastContainer position="top-right" autoClose={2000} />
      <Header
        isAuthenticated={true}
        onLoginClick={() => navigate('/login')}
        onHomeClick={() => navigate('/')}
        onLogoutClick={handleLogout}
      />

      <h2 className="devis-title" style={{ color: '#424272' }}>Liste des Devis Foodinnov :</h2>

      <div className="opportunites-actions-bar">
        {userRole && userRole !== 'foodinnov' && (
          <button
            className="add-opportunite-button"
            onClick={handleAdd}
            style={{ backgroundColor: '#424272' }}
          >
            + Ajouter un Devis
          </button>
        )}
        <button
          className="add-opportunite-button"
          onClick={handleExportCSV}
          style={{ backgroundColor: '#424272' }}
        >
          Exporter en CSV
        </button>

        <select
          className="opportunites-search-bar filter-select"
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setCurrentPage(1);
          }}
          style={{ maxWidth: "180px" }}
        >
          <option value="">Filtrer par type --</option>
          <option value="all">Tous les types</option>
          {uniqueOpportunityTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="opportunites-search-bar"
          placeholder="Rechercher... "
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <button type="button" title="Mois précédent" onClick={() => { if (selectedMonth === 'all') return; const m = Number(selectedMonth); setSelectedMonth(m === 0 ? 11 : m - 1); setCurrentPage(1); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <select
            className="opportunites-search-bar"
            style={{ maxWidth: "130px" }}
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(e.target.value === "all" ? "all" : Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="all">Afficher Tout</option>
            {[...Array(12).keys()].map((monthIndex) => (
              <option key={monthIndex} value={monthIndex}>
                {new Date(0, monthIndex).toLocaleString("fr-FR", { month: "long" }).replace(/^\w/, c => c.toUpperCase())}
              </option>
            ))}
          </select>
          <button type="button" title="Mois suivant" onClick={() => { if (selectedMonth === 'all') return; const m = Number(selectedMonth); setSelectedMonth(m === 11 ? 0 : m + 1); setCurrentPage(1); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        <select
          className="opportunites-search-bar"
          style={{ maxWidth: "120px" }}
          value={selectedYear}
          onChange={(e) => {
            setSelectedYear(e.target.value === "all" ? "all" : Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value="all">Afficher Tout</option>
          {[...Array(5).keys()].map((i) => {
            const year = new Date().getFullYear() - 2 + i; 
            return (
              <option key={year} value={year}>
                {year}
              </option>
            );
          })}
        </select>

        <select
          className="opportunites-search-bar"
          style={{ maxWidth: "160px" }}
          value={sortField}
          onChange={(e) => {
            setSortField(e.target.value as "date" | "updatedAt" | "none");
            setCurrentPage(1);
          }}
        >
          <option value="date">Date de création</option>
          <option value="updatedAt">Date d'édition</option>
        </select>

        <button
          style={{
            margin: "0 0.5rem",
            padding: "0.3rem 0.6rem",
            background: "#424272",
            color: "white",
            border: "none",
            borderRadius: "4px"
          }}
          onClick={() =>
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
          }
        >
          {sortDirection === "asc" ? (
            "↑"
          ) : (
            "↓"
          )}
        </button>

        <select
          className="opportunites-search-bar"
          style={{ maxWidth: "130px" }}
          value={devisPerPage}
          onChange={(e) => {
            setDevisPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={5}>5 par page</option>
          <option value={10}>10 par page</option>
          <option value={20}>20 par page</option>
          <option value={50}>50 par page</option>
        </select>
      </div>

      <table className="opportunites-table">
        <thead>
          <tr>
            <th>Numéro</th>
            <th>Société</th>
            <th>Contact</th>
            <th>Type</th>
            <th>Montant (HT)</th>
            <th>Montant (TTC)</th>
            <th>Date</th>
            <th>Édité le</th>
            <th>Documents</th>
            {userRole !== "foodinnov" && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {currentDevis.length === 0 ? (
            <tr>
              <td colSpan={10} style={{ textAlign: "center" }}>
                Aucun devis.
              </td>
            </tr>
          ) : (
            currentDevis.map((d, i) => (
              <tr key={i}>
                <td>{d.id || "-"}</td>
                <td>{d.companyId}</td>
                <td>
                  <button onClick={() => openContactModal(d)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#007bff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    {d.contact}
                  </button>
                </td>
                <td>{d.opportunityType}</td>
                <td>{d.amount.toFixed(2)} €</td>
                <td>{((d.amount || 0) * 1.2).toFixed(2)} €</td>
                <td>
                  {d.date instanceof Timestamp
                    ? d.date.toDate().toLocaleDateString("fr-FR")
                    : new Date(d.date).toLocaleDateString("fr-FR")}
                </td>
                <td>
                  {d.updatedAt
                    ? d.updatedAt instanceof Timestamp
                      ? d.updatedAt.toDate().toLocaleDateString("fr-FR")
                      : new Date(d.updatedAt).toLocaleDateString("fr-FR")
                    : "-"}
                </td>
                <td className="documents-column">
                  <i
                    className="bi bi-filetype-pdf"
                    title="PDF Standard"
                    onClick={() => {
                      console.log("Clic sur icône PDF Standard");
                      fillPdfWithData(d);
                    }}
                    style={{ cursor: "pointer", marginRight: "10px", fontSize: "1.2rem", color: "#0d6efd" }}
                  />
                  {d.opportunityType === "Enregistrement FDA (USA)" && (
                    <i
                      className="bi bi-filetype-pdf"
                      title="PDF FDA"
                      onClick={() => fillFdaPdfWithData(d)}
                      style={{ color: 'red', cursor: "pointer", fontSize: "1.2rem", marginRight: "10px" }}
                    />
                  )}
                  {d.signedFileUrl && (
                    <i
                      className="bi bi-filetype-pdf"
                      title="PDF signé uploadé"
                      onClick={() => {
                        setPdfPreviewUrl(d.signedFileUrl.startsWith('http') ? d.signedFileUrl : window.location.origin + d.signedFileUrl);
                        setShowPdfModal(true);
                        setCurrentPdfDevis(d);
                      }}
                      style={{ color: 'green', cursor: "pointer", fontSize: "1.2rem" }}
                    />
                  )}
                </td>
                {userRole !== "foodinnov" && (
                  <td className="actions-column">
                    <button className="edit-button" onClick={() => handleEdit(d)}><i className="bi bi-pencil-square"></i></button>
                    <button className="delete-button" onClick={() => handleDelete(d)}><i className="bi bi-trash"></i></button>
                    <button 
                      className="edit-button" 
                      onClick={() => navigate('/factures-foodinnov', { state: { preselectedDevis: d } })} 
                      title="Créer une facture pour ce devis"
                      style={{ marginLeft: '5px' }}
                    >
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  </td>
                )}
              </tr>
            ))
          )}
          <tr style={{ fontWeight: "bold", background: "#f8f8f8" }}>
            <td colSpan={4}>Total</td>
            <td>{totalAmount.toFixed(2)} €</td>
            <td>{totalAmountTTC.toFixed(2)} €</td>
            {userRole !== "foodinnov" ? (
              <td colSpan={4}></td>
            ) : (
              <td colSpan={3}></td>
            )}
          </tr>
        </tbody>
      </table>

      <div className="pagination">
        <button onClick={prevPage} disabled={currentPage === 1}>Précédent</button>
        <span>Page {currentPage} / {totalPages}</span>
        <button onClick={nextPage} disabled={currentPage === totalPages}>Suivant</button>
      </div>

      <DevisFoodinnovModal
        show={showModal}
        handleClose={handleCloseModal}
        initialData={devisToEdit}
        preselectedOpportunity={preselectedOpportunity}
      />

      {showPdfModal && pdfPreviewUrl && (
        <div className="pdf-modal-overlay">
          <div className="pdf-modal-content">
            <div className="pdf-modal-header">
              <h2>Aperçu PDF {currentPdfDevis?.signedFileUrl === pdfPreviewUrl ? 'signé' : `#${currentPdfDevis?.id}`}</h2>
              <button className="close-btn" onClick={() => setShowPdfModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <iframe
                src={pdfPreviewUrl}
                title="Aperçu PDF"
                width="100%"
              />
            </div>
            <div className="pdf-modal-footer">
              <button onClick={() => setShowPdfModal(false)} className="button">Fermer</button>
              <a
                href={pdfPreviewUrl}
                download={`PDF_${currentPdfDevis?.id || 'signed'}.pdf`}
                className="download-btn"
              >
                Télécharger PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {showFdaPdfModal && fdaPdfPreviewUrl && (
        <div className="pdf-modal-overlay">
          <div className="pdf-modal-content">
            <div className="pdf-modal-header">
              <h2>Aperçu Devis FDA #{currentPdfDevis?.id}</h2>
              <button className="close-btn" onClick={() => setShowFdaPdfModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
            <iframe
              src={fdaPdfPreviewUrl}
              title="Aperçu PDF FDA"
              width="100%"
            />
            </div>
            <div className="pdf-modal-footer">
              <button onClick={() => setShowFdaPdfModal(false)} className="button">Fermer</button>
              <a
                href={fdaPdfPreviewUrl}
                download={currentPdfDevis ? generateFdaPdfFileName(currentPdfDevis) : 'Hillebrand-FOODINNOV_sans_nom.pdf'}
                className="download-btn"
              >
                Télécharger PDF FDA
              </a>
            </div>
          </div>
        </div>
      )}

      {devisFoodinnovToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer le devis "{devisFoodinnovToDelete?.opportunityType ?? ""}" pour la société "{devisFoodinnovToDelete?.companyId ?? ""}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setDevisFoodinnovToDelete(null)}>
                Annuler
              </button>
              <button className="delete-button" onClick={confirmDevisFoodinnovDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {contactModal && (
        <div className="contact-popup-overlay" onClick={closeContactModal}>
          <div className="contact-popup-card" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup-btn" onClick={closeContactModal}>✕</button>
            <div className="contact-popup-content">
              <div className={`contact-avatar ${
                contactModal.gender === "Mme" ? "avatar-female"
                : contactModal.gender === "Mr" ? "avatar-male"
                : "avatar-neutral"
              }`}>
                {((contactModal.principalContact || "").charAt(0) + (contactModal.principalFirstName || "").charAt(0)).toUpperCase()}
              </div>
              <h3>{contactModal.name}</h3>
              {contactModal.fonction && <p className="contact-role">{contactModal.fonction}</p>}
              <hr />
              <div className="contact-info">
                <p><strong>Téléphone :</strong> {contactModal.phone || "Non renseigné"}</p>
                <p><strong>Email :</strong> {contactModal.email || "Non renseigné"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DevisFoodinnov;
